import ParentService from "@/services/ParentService";

const dashboardDefault = {
  overallStatus: {
    title: "Дані ще не завантажені",
    description: "Показники з'являться після успішного завантаження даних.",
    updatedAt: "Оновлено щойно",
  },
  metrics: [],
  attentionItems: [],
  subjects: [],
  progress: {
    period: "",
    note: "",
    points: [],
  },
  recentEvents: [],
  recentEventsHasMore: false,
  recentEventsCount: 0,
  reportCard: {
    state: "empty",
    title: "Табель ще не завантажено",
    period: "",
    updatedAt: "",
    description: "",
    rows: [],
    summary: {
      subjectsCount: 0,
      subjectsWithGrades: 0,
      averageGrade: null,
    },
  },
};

const courseOverviewDefault = {
  id: null,
  name: "Предмет",
  teacher: "",
  className: "",
  academicYear: "",
  status: {
    type: "insufficient_data",
    title: "Завантаження даних",
    summary: "Інформація про предмет оновлюється.",
  },
  reasons: [],
  topics: [],
  progress: {
    period: "",
    note: "",
    points: [],
  },
  recentEvents: [],
  recentEventsHasMore: false,
};

const periodOptions = [
  {
    id: "week",
    label: "Цей тиждень",
  },
  {
    id: "month",
    label: "Цей місяць",
  },
  {
    id: "semester",
    label: "Семестр",
  },
  {
    id: "school_year",
    label: "Навчальний рік",
  },
];

const progressRangeOptions = [
  {
    id: "last_4_months",
    label: "Останні 4 місяці",
  },
  {
    id: "last_6_months",
    label: "Останні 6 місяців",
  },
  {
    id: "semester",
    label: "Семестр",
  },
  {
    id: "school_year",
    label: "Навчальний рік",
  },
];

const activityPeriodOptions = [
  {
    id: "all",
    label: "Увесь час",
  },
  {
    id: "today",
    label: "Сьогодні",
  },
  {
    id: "7",
    label: "Останні 7 днів",
  },
  {
    id: "month",
    label: "Поточний місяць",
  },
  {
    id: "semester",
    label: "Поточний семестр",
  },
  {
    id: "school_year",
    label: "Навчальний рік",
  },
];

const activityTypeOptions = [
  {
    id: "all",
    label: "Усі типи",
    icon: "mdi-view-list-outline",
    theme: "all",
  },
  {
    id: "homework",
    label: "Домашні завдання",
    icon: "mdi-clipboard-text-outline",
    theme: "green",
  },
  {
    id: "homework_pending",
    label: "Домашнє завдання не виконано",
    icon: "mdi-clipboard-remove-outline",
    theme: "orange",
  },
  {
    id: "homework_rework",
    label: "Домашнє завдання повернуто на доопрацювання",
    icon: "mdi-undo-variant",
    theme: "orange",
  },
  {
    id: "homework_graded",
    label: "Домашнє завдання з оцінкою",
    icon: "mdi-clipboard-check-outline",
    theme: "green",
  },
  {
    id: "homework_awaiting_grade",
    label: "Домашнє завдання очікує оцінки",
    icon: "mdi-timer-sand",
    theme: "orange",
  },
  {
    id: "message",
    label: "Повідомлення",
    icon: "mdi-message-text-outline",
    theme: "purple",
  },
  {
    id: "report",
    label: "Табелі",
    icon: "mdi-file-document-outline",
    theme: "blue",
  },
];

const activityCenterDefault = {
  title: "Активність дитини",
  subtitle:
    "Усі оцінки, домашні завдання, повідомлення та відвідування в одному місці",
  filters: {
    search: "",
    period: "month",
    type: "all",
    status: "all",
    subject: "all",
    date: null,
  },
  periodOptions: activityPeriodOptions,
  typeOptions: activityTypeOptions,
  subjectOptions: [
    {
      id: "all",
      label: "Усі предмети",
    },
  ],
  calendar: {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    selectedDate: null,
    eventDates: [],
  },
  pagination: {
    page: 1,
    pageSize: 30,
    totalPages: 1,
    count: 0,
    pageSizeOptions: [10, 30, 50, 100],
  },
  events: [],
  totalCount: 0,
  hasMore: false,
  typeCounts: [],
};

const diaryDefault = {
  period: {
    dateFrom: null,
    dateTo: null,
  },
  student: null,
  events: [],
};

const attentionIconByType = {
  grade_drop: "mdi-book-open-page-variant-outline",
  homework: "mdi-clipboard-alert-outline",
  low_grade: "mdi-book-alert-outline",
};

const subjectThemes = ["blue", "green", "orange", "purple", "teal"];
const subjectIcons = [
  "mdi-book-open-variant",
  "mdi-tree-outline",
  "mdi-dna",
  "mdi-book-alphabet",
  "mdi-bank-outline",
];

function getDeltaType(delta) {
  if (delta > 0) {
    return "up";
  }

  if (delta < 0) {
    return "down";
  }

  return "same";
}

function formatDelta(delta) {
  if (delta === null || delta === undefined) {
    return "";
  }

  if (delta > 0) {
    return `+${delta}`;
  }

  return `${delta}`;
}

function formatGrade(value) {
  return value === null || value === undefined ? "-" : `${value}`;
}

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("uk-UA");
}

function formatAcademicYear(academicYear) {
  if (!academicYear || !academicYear.year) {
    return "";
  }

  return `${academicYear.year}/${Number(academicYear.year) + 1}`;
}

function formatMessagesCount(count) {
  const normalizedCount = count || 0;
  const lastTwoDigits = normalizedCount % 100;
  const lastDigit = normalizedCount % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return `${normalizedCount} повідомлень`;
  }

  if (lastDigit === 1) {
    return `${normalizedCount} повідомлення`;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${normalizedCount} повідомлення`;
  }

  return `${normalizedCount} повідомлень`;
}

function createActivityCenterState() {
  return {
    ...activityCenterDefault,
    filters: {
      ...activityCenterDefault.filters,
    },
    periodOptions: activityCenterDefault.periodOptions.map((option) => ({
      ...option,
    })),
    typeOptions: activityCenterDefault.typeOptions.map((option) => ({
      ...option,
    })),
    subjectOptions: activityCenterDefault.subjectOptions.map((option) => ({
      ...option,
    })),
    calendar: {
      ...activityCenterDefault.calendar,
      eventDates: [...activityCenterDefault.calendar.eventDates],
    },
    pagination: {
      ...activityCenterDefault.pagination,
      pageSizeOptions: [...activityCenterDefault.pagination.pageSizeOptions],
    },
    events: activityCenterDefault.events.map(normalizeActivityEvent),
  };
}

function getActivitySubjectName(event) {
  if (!event.subject) {
    return "";
  }

  return typeof event.subject === "object" ? event.subject.name : event.subject;
}

function getActivitySubjectId(event) {
  if (!event.subject || typeof event.subject !== "object") {
    return event.subject;
  }

  return event.subject.id;
}

function normalizeActivityEvent(event) {
  const subject =
    event.subject && typeof event.subject === "object"
      ? event.subject
      : {
          id: event.subject || null,
          name: event.subject || "",
        };
  const action = event.action || {};

  return {
    ...event,
    subject,
    action,
    actionLabel: event.actionLabel || action.label || "",
    target: event.target || {
      module: action.module,
      ...(action.params || {}),
    },
  };
}

function getDateValue(date) {
  return new Date(`${date}T00:00:00`).getTime();
}

function getPeriodStartDate(currentDate, daysCount) {
  const date = new Date(`${currentDate}T00:00:00`);
  date.setDate(date.getDate() - daysCount + 1);
  return date.toISOString().slice(0, 10);
}

function matchesActivitySearch(event, search) {
  const normalizedSearch = search.trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  const haystack = [
    event.title,
    event.description,
    getActivitySubjectName(event),
    event.teacher,
    event.topic,
    event.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalizedSearch);
}

function matchesActivityPeriod(event, period, currentDate) {
  if (period === "7" || period === "30") {
    const startDate = getPeriodStartDate(currentDate, Number(period));
    return getDateValue(event.date) >= getDateValue(startDate);
  }

  return true;
}

function filterActivityEvents(activityCenter) {
  const { filters, events, currentDate } = activityCenter;

  return events.filter((event) => {
    // Teacher comments are intentionally not part of the parent activity centre.
    if (event.type === "teacher_comment") {
      return false;
    }

    if (!matchesActivitySearch(event, filters.search)) {
      return false;
    }

    if (
      filters.period !== "all" &&
      !matchesActivityPeriod(event, filters.period, currentDate)
    ) {
      return false;
    }

    if (filters.type !== "all") {
      const homeworkStateByType = {
        homework_pending: "pending",
        homework_rework: "rework",
        homework_graded: "graded",
        homework_awaiting_grade: "awaiting_grade",
      };
      const homeworkState = homeworkStateByType[filters.type];
      const matchesType = homeworkState
        ? event.type === "homework" && event.homeworkState === homeworkState
        : event.type === filters.type;

      if (!matchesType) {
        return false;
      }
    }

    if (filters.status !== "all" && event.homeworkState !== filters.status) {
      return false;
    }

    if (
      filters.subject !== "all" &&
      `${getActivitySubjectId(event)}` !== `${filters.subject}` &&
      getActivitySubjectName(event) !== filters.subject
    ) {
      return false;
    }

    if (filters.date && event.date !== filters.date) {
      return false;
    }

    return true;
  });
}

function groupActivityEvents(events) {
  return events.reduce((groups, event) => {
    const existingGroup = groups.find((group) => group.id === event.group);

    if (existingGroup) {
      existingGroup.events.push(event);
    } else {
      groups.push({
        id: event.group,
        label: event.group,
        events: [event],
      });
    }

    return groups;
  }, []);
}

function getOptionLabel(options, id) {
  const option = options.find((item) => `${item.id}` === `${id}`);
  return option ? option.label : "";
}

function getActivityCenterRequestParams(activityCenter) {
  const { filters } = activityCenter;

  return {
    period: filters.period,
    search: filters.search,
    types: filters.type === "all" ? "" : filters.type,
    status: filters.status === "all" ? "" : filters.status,
    subject: filters.subject,
    date: filters.date,
    calendar_year: activityCenter.calendar.year,
    calendar_month: activityCenter.calendar.month,
    page: activityCenter.pagination.page,
    page_size: activityCenter.pagination.pageSize,
  };
}

function mapActivityCenterResponse(responseData, previousActivityCenter) {
  const filters = responseData.filters || {};
  const calendar = responseData.calendar || {};

  return {
    ...previousActivityCenter,
    period: responseData.period || previousActivityCenter.period,
    periodOptions:
      filters.period_options || previousActivityCenter.periodOptions,
    typeOptions: (filters.type_options || previousActivityCenter.typeOptions)
      .filter((option) => option.id !== "teacher_comment"),
    subjectOptions:
      filters.subject_options || previousActivityCenter.subjectOptions,
    calendar: {
      year: calendar.year || previousActivityCenter.calendar.year,
      month: calendar.month || previousActivityCenter.calendar.month,
      selectedDate:
        calendar.selected_date || previousActivityCenter.calendar.selectedDate,
      eventDates:
        calendar.event_dates || previousActivityCenter.calendar.eventDates,
    },
    events: (responseData.results || [])
      .filter((event) => event.type !== "teacher_comment")
      .map(normalizeActivityEvent),
    totalCount: responseData.count !== undefined ? responseData.count : 0,
    hasMore: responseData.has_more || false,
    pagination: {
      ...previousActivityCenter.pagination,
      page: responseData.page || previousActivityCenter.pagination.page,
      pageSize:
        responseData.page_size || previousActivityCenter.pagination.pageSize,
      totalPages: responseData.total_pages || 1,
      count: responseData.count !== undefined ? responseData.count : 0,
    },
    typeCounts: (responseData.type_counts || [])
      .filter((type) => type.id !== "teacher_comment"),
  };
}

function mapDashboardResponse(responseData, previousDashboard) {
  const metrics = responseData.metrics || {};
  const averageGrade = metrics.average_grade || {};
  const homeworks = metrics.homeworks || {};
  const messages = metrics.messages || {};

  return {
    ...previousDashboard,
    period: responseData.period,
    overallStatus: {
      title: responseData.overall_status.title,
      description: responseData.overall_status.description,
      type: responseData.overall_status.type,
      score: responseData.overall_status.score,
      explanation: responseData.overall_status.explanation
        ? {
            weightedScore: responseData.overall_status.explanation.weighted_score,
            finalScore: responseData.overall_status.explanation.final_score,
            components: responseData.overall_status.explanation.components || [],
            appliedRules: responseData.overall_status.explanation.applied_rules || [],
            thresholds: responseData.overall_status.explanation.thresholds || [],
          }
        : null,
    },
    metrics: [
      {
        title: "Середній бал",
        value: formatGrade(averageGrade.value),
        delta: formatDelta(averageGrade.delta),
        deltaType: getDeltaType(averageGrade.delta),
        description: `${averageGrade.grades_count || 0} оцінок за період`,
        icon: "mdi-medal-outline",
        theme: "green",
      },
      {
        title: "Домашні завдання",
        value: `${homeworks.completed_count || 0} із ${homeworks.assigned_count || 0}`,
        delta: "виконано",
        deltaType: homeworks.not_submitted ? "warn" : "up",
        description: `${homeworks.not_submitted || 0} незданих, ${
          homeworks.needs_rework || 0
        } потребує доопрацювання`,
        icon: "mdi-clipboard-text-outline",
        theme: "orange",
      },
      {
        title: "Повідомлення",
        value: `${messages.unread_count || 0}`,
        delta: "непрочитаних",
        deltaType: "",
        description: `усього ${formatMessagesCount(messages.total_count)}`,
        icon: "mdi-message-text-outline",
        theme: "purple",
      },
    ],
    attentionItems: (responseData.attention_required || []).map((item) => ({
      type: item.type,
      courseId: item.course_id,
      title: item.title,
      description: item.description,
      detail: `${item.detail}`,
      detailType: item.severity === "danger" ? "danger" : "warning",
      icon: attentionIconByType[item.type] || "mdi-alert-outline",
      iconTheme: item.severity === "danger" ? "red" : "orange",
    })),
    subjects: (responseData.subjects || []).map((subject, index) => ({
      id: subject.id,
      name: subject.name,
      score: formatGrade(subject.score),
      delta: formatDelta(subject.delta),
      deltaType: subject.delta_type || getDeltaType(subject.delta),
      gradesCount: `${subject.grades_count || 0} оцінок`,
      icon: subject.icon || subjectIcons[index % subjectIcons.length],
      theme: subjectThemes[index % subjectThemes.length],
    })),
  };
}

function mapProgressResponse(responseData, previousDashboard) {
  const points = responseData.points || [];

  return {
    ...previousDashboard,
    progress: {
      period: responseData.range ? responseData.range.label : "",
      note: responseData.note || "",
      delta: responseData.delta,
      points: points.map((point) => ({
        label: point.label,
        value: point.value,
        gradesCount: point.grades_count,
        dateFrom: point.date_from,
        dateTo: point.date_to,
      })),
    },
  };
}

function mapRecentEventsResponse(responseData, previousDashboard) {
  return {
    ...previousDashboard,
    recentEvents: (responseData.results || []).map((event, index) => ({
      id:
        event.id || `${event.type}-${event.created_at || event.time}-${index}`,
      type: event.type,
      title: event.title,
      subtitle: event.subtitle,
      time: event.time,
      value: event.value,
      icon: event.icon,
      theme: event.theme,
      isRead: event.is_read,
    })),
    recentEventsHasMore: responseData.has_more || false,
    recentEventsCount: responseData.count || 0,
  };
}

function formatCourseEventDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("uk-UA", {
    day: "numeric",
    month: "long",
  });
}

function mapCourseOverviewResponse(responseData, dashboard) {
  const dashboardCourse = dashboard.subjects.find(
    (subject) => `${subject.id}` === `${responseData.id}`
  );
  const status = responseData.status || courseOverviewDefault.status;
  const statusExplanation = status.explanation;

  return {
    id: responseData.id,
    name:
      responseData.name || (dashboardCourse ? dashboardCourse.name : "Предмет"),
    icon:
      responseData.icon ||
      (dashboardCourse ? dashboardCourse.icon : "mdi-book-open-variant"),
    teacher: responseData.teacher || "Вчителя не вказано",
    className: responseData.class_name || "",
    academicYear: responseData.academic_year || "",
    period: responseData.period || {},
    status: {
      ...status,
      explanation: statusExplanation
        ? {
            weightedScore: statusExplanation.weighted_score,
            finalScore: statusExplanation.final_score,
            components: statusExplanation.components || [],
            appliedRules: statusExplanation.applied_rules || [],
            thresholds: statusExplanation.thresholds || [],
          }
        : null,
    },
    reasons: (responseData.reasons || []).map((reason, index) => ({
      id: reason.id || `${reason.type || "reason"}-${index}`,
      type: reason.type,
      icon: reason.icon || "mdi-information-outline",
      title: reason.title,
      description: reason.description,
      value: reason.value,
    })),
    topics: (responseData.topics || []).map((topic) => ({
      id: topic.id,
      name: topic.name,
      score: topic.score,
      description: topic.description,
      status: topic.status,
      statusLabel: topic.status_label,
    })),
    progress: {
      period: responseData.progress?.range?.label || "",
      note: responseData.progress?.note || "",
      delta: responseData.progress?.delta,
      points: (responseData.progress?.points || []).map((point) => ({
        label: point.label,
        value: point.value,
        gradesCount: point.grades_count,
        dateFrom: point.date_from,
        dateTo: point.date_to,
      })),
    },
    recentEvents: (responseData.recent_events || []).map((event) => ({
      id: event.id,
      type: event.type,
      theme: event.theme,
      icon: event.icon,
      value: event.value,
      title: event.title,
      subtitle: event.subtitle || event.description,
      importance: event.importance,
      attemptsCount: event.attempts_count || 1,
      dateLabel: event.date_label
        ? formatCourseEventDate(event.date_label)
        : formatCourseEventDate(event.date),
      details: event.description || event.subtitle || "",
      action: event.action,
    })),
    recentEventsHasMore: responseData.recent_events_has_more || false,
    generatedAt: responseData.generated_at,
  };
}

function mapReportCardResponse(responseData, previousDashboard) {
  const academicYear = responseData.academic_year || {};
  const summary = responseData.summary || {};
  const updatedAt = formatDate(responseData.updated_at);
  const periodLabel = formatAcademicYear(academicYear);

  return {
    ...previousDashboard,
    reportCard: {
      state: responseData.state,
      title:
        responseData.state === "ready"
          ? "Табель"
          : responseData.title || "Табель ще не готовий",
      description: responseData.description || "",
      period: periodLabel ? `Навчальний рік ${periodLabel}` : "",
      updatedAt: updatedAt
        ? `Оновлено: ${updatedAt}`
        : "Оцінок для оновлення ще немає",
      generatedAt: responseData.generated_at,
      student: responseData.student || {},
      academicYear,
      rows: (responseData.rows || []).map((row) => ({
        number: row.number,
        subject: row.subject,
        firstSemester: formatGrade(row.first_semester),
        firstSemesterGradesCount: row.first_semester_grades_count || 0,
        secondSemester: formatGrade(row.second_semester),
        secondSemesterGradesCount: row.second_semester_grades_count || 0,
        year: formatGrade(row.year),
        yearGradesCount: row.year_grades_count || 0,
      })),
      summary: {
        subjectsCount: summary.subjects_count || 0,
        subjectsWithGrades: summary.subjects_with_grades || 0,
        averageGrade: summary.average_grade,
      },
    },
  };
}

function createState() {
  return {
    children: [],
    activeChildId: null,
    activePeriodId: "month",
    periodOptions,
    activeProgressRangeId: "last_4_months",
    progressRangeOptions,
    courses: [],
    dashboard: dashboardDefault,
    courseOverview: { ...courseOverviewDefault },
    activityCenter: createActivityCenterState(),
    diary: { ...diaryDefault, period: { ...diaryDefault.period }, events: [] },
    lastLoadedAt: new Date(),
    loading: false,
    coursesLoading: false,
    dashboardLoading: false,
    courseOverviewLoading: false,
    progressLoading: false,
    recentEventsLoading: false,
    activityCenterLoading: false,
    diaryLoading: false,
    reportCardLoading: false,
    reportCardDownloading: false,
    error: null,
    coursesError: null,
    dashboardError: null,
    courseOverviewError: null,
    progressError: null,
    recentEventsError: null,
    activityCenterError: null,
    diaryError: null,
    reportCardError: null,
  };
}

const state = createState();

const getters = {
  getChildren(state) {
    return state.children;
  },
  getActiveChildId(state) {
    return state.activeChildId;
  },
  getActiveChild(state) {
    return state.children.find((child) => {
      return child.id === state.activeChildId;
    });
  },
  getPeriodOptions(state) {
    return state.periodOptions;
  },
  getActivePeriodId(state) {
    return state.activePeriodId;
  },
  getActivePeriod(state) {
    return state.periodOptions.find((period) => {
      return period.id === state.activePeriodId;
    });
  },
  getProgressRangeOptions(state) {
    return state.progressRangeOptions;
  },
  getActiveProgressRangeId(state) {
    return state.activeProgressRangeId;
  },
  getActiveProgressRange(state) {
    return state.progressRangeOptions.find((range) => {
      return range.id === state.activeProgressRangeId;
    });
  },
  getCourses(state) {
    return state.courses;
  },
  getDashboard(state) {
    return state.dashboard;
  },
  getCourseOverview(state) {
    return (courseId) => {
      const course = state.dashboard.subjects.find(
        (subject) => `${subject.id}` === `${courseId}`
      );
      const hasLoadedOverview = `${state.courseOverview.id}` === `${courseId}`;

      return {
        ...state.courseOverview,
        id: hasLoadedOverview
          ? state.courseOverview.id
          : course
          ? course.id
          : courseId,
        name: hasLoadedOverview
          ? state.courseOverview.name
          : course
          ? course.name
          : courseOverviewDefault.name,
        icon: hasLoadedOverview
          ? state.courseOverview.icon || "mdi-book-open-variant"
          : course
          ? course.icon
          : "mdi-book-open-variant",
        reasons: state.courseOverview.reasons.map((reason) => ({ ...reason })),
        topics: state.courseOverview.topics.map((topic) => ({ ...topic })),
        recentEvents: state.courseOverview.recentEvents.map((event) => ({
          ...event,
        })),
      };
    };
  },
  isCourseOverviewLoading(state) {
    return state.courseOverviewLoading;
  },
  getCourseOverviewError(state) {
    return state.courseOverviewError;
  },
  getActivityCenter(state) {
    return state.activityCenter;
  },
  getDiary(state) {
    return state.diary;
  },
  isDiaryLoading(state) {
    return state.diaryLoading;
  },
  getDiaryError(state) {
    return state.diaryError;
  },
  getActivityCenterEvents(state) {
    return state.activityCenter.events;
  },
  getActivityCenterFilteredEvents(state) {
    return filterActivityEvents(state.activityCenter);
  },
  getActivityCenterGroups(state, getters) {
    return groupActivityEvents(getters.getActivityCenterFilteredEvents);
  },
  getActivityCenterVisibleCount(state, getters) {
    return (
      state.activityCenter.pagination.count ||
      getters.getActivityCenterFilteredEvents.length
    );
  },
  getActivityCenterPagination(state) {
    return state.activityCenter.pagination;
  },
  getActivityCenterActiveFilterChips(state) {
    const {
      filters,
      periodOptions,
      typeOptions,
      subjectOptions,
    } = state.activityCenter;
    const chips = [];

    if (filters.search.trim()) {
      chips.push({
        id: "search",
        label: `Пошук: ${filters.search.trim()}`,
      });
    }

    if (filters.period !== "all") {
      chips.push({
        id: "period",
        label: getOptionLabel(periodOptions, filters.period),
      });
    }

    if (filters.type !== "all") {
      chips.push({
        id: "type",
        label: getOptionLabel(typeOptions, filters.type),
      });
    }

    if (filters.status !== "all") {
      chips.push({
        id: "status",
        label: filters.status === "pending" ? "Не виконано" : "Виконано",
      });
    }

    if (filters.subject !== "all") {
      chips.push({
        id: "subject",
        label: getOptionLabel(subjectOptions, filters.subject),
      });
    }

    if (filters.date) {
      chips.push({
        id: "date",
        label: `Дата: ${filters.date.split("-").reverse().join(".")}`,
      });
    }

    return chips;
  },
  getActivityCenterTypeCounts(state) {
    if (
      state.activityCenter.typeCounts &&
      state.activityCenter.typeCounts.length
    ) {
      return state.activityCenter.typeCounts;
    }

    return state.activityCenter.typeOptions.map((typeOption) => {
      const homeworkStateByType = {
        homework_pending: "pending",
        homework_rework: "rework",
        homework_graded: "graded",
        homework_awaiting_grade: "awaiting_grade",
      };
      const homeworkState = homeworkStateByType[typeOption.id];
      const count = typeOption.id === "all"
        ? state.activityCenter.events.length
        : homeworkState
          ? state.activityCenter.events.filter(
              (event) => event.type === "homework" && event.homeworkState === homeworkState
            ).length
          : state.activityCenter.events.filter(
              (event) => event.type === typeOption.id
            ).length;

      return {
        ...typeOption,
        count,
      };
    });
  },
  getActivityCenterCalendarEventDates(state) {
    return state.activityCenter.calendar.eventDates;
  },
  getLastLoadedAt(state) {
    return state.lastLoadedAt;
  },
  isLoading(state) {
    return state.loading;
  },
  isCoursesLoading(state) {
    return state.coursesLoading;
  },
  isDashboardLoading(state) {
    return state.dashboardLoading;
  },
  isProgressLoading(state) {
    return state.progressLoading;
  },
  isRecentEventsLoading(state) {
    return state.recentEventsLoading;
  },
  isActivityCenterLoading(state) {
    return state.activityCenterLoading;
  },
  isReportCardLoading(state) {
    return state.reportCardLoading;
  },
  isReportCardDownloading(state) {
    return state.reportCardDownloading;
  },
  getError(state) {
    return state.error;
  },
  getCoursesError(state) {
    return state.coursesError;
  },
  getDashboardError(state) {
    return state.dashboardError;
  },
  getProgressError(state) {
    return state.progressError;
  },
  getRecentEventsError(state) {
    return state.recentEventsError;
  },
  getActivityCenterError(state) {
    return state.activityCenterError;
  },
  getReportCardError(state) {
    return state.reportCardError;
  },
};

const actions = {
  async selectChild({ commit, dispatch }, childId) {
    commit("setActiveChildId", childId);
    commit("setCourseOverview", { ...courseOverviewDefault });
    await dispatch("loadActiveChildData");
  },
  async selectPeriod({ commit, dispatch }, periodId) {
    commit("setActivePeriodId", periodId);
    await dispatch("loadActiveChildDashboard");
  },
  async selectProgressRange({ commit, dispatch }, rangeId) {
    commit("setActiveProgressRangeId", rangeId);
    await dispatch("loadActiveChildProgress");
  },
  async setActivitySearch({ commit, dispatch }, search) {
    commit("setActivityFilter", {
      key: "search",
      value: search,
    });
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivityPeriod({ commit, dispatch }, periodId) {
    commit("setActivityFilter", {
      key: "period",
      value: periodId,
      resetDate: true,
    });
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivityType({ commit, dispatch }, typeId) {
    const selectedType = ["teacher_comment", "grade"].includes(typeId)
      ? "all"
      : typeId;
    commit("setActivityFilter", {
      key: "type",
      value: selectedType,
    });
    if (selectedType !== "homework") {
      commit("setActivityFilter", { key: "status", value: "all" });
    }
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivityStatus({ commit, dispatch }, status) {
    commit("setActivityFilter", { key: "status", value: status });
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivitySubject({ commit, dispatch }, subjectId) {
    commit("setActivityFilter", {
      key: "subject",
      value: subjectId,
    });
    await dispatch("loadActiveChildActivityCenter");
  },
  async applyActivityContext({ commit, dispatch }, context) {
    commit("setActivityContext", {
      ...context,
      type: ["teacher_comment", "grade"].includes(context.type)
        ? "all"
        : context.type,
    });
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivityDate({ commit, dispatch }, date) {
    commit("setActivityDate", date);
    await dispatch("loadActiveChildActivityCenter");
  },
  async clearActivityFilters({ commit, dispatch }) {
    commit("clearActivityFilters");
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivityPage({ commit, dispatch }, page) {
    commit("setActivityPage", page);
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivityPageSize({ commit, dispatch }, pageSize) {
    commit("setActivityPageSize", pageSize);
    await dispatch("loadActiveChildActivityCenter");
  },
  async selectActivityCalendarMonth({ commit, dispatch }, payload) {
    commit("setActivityCalendarMonth", payload);
    await dispatch("loadActiveChildActivityCenter");
  },
  async loadChildren({ commit, dispatch }) {
    commit("setLoading", true);
    commit("setError", null);

    try {
      const response = await ParentService.getChildren();
      commit("setChildren", response.data || []);
      await dispatch("loadActiveChildData");
      return response.data;
    } catch (error) {
      commit("setError", error);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },
  async loadActiveChildCourses({ commit, state }) {
    if (!state.activeChildId) {
      commit("setCourses", []);
      commit("setLastLoadedAt", new Date());
      return [];
    }

    commit("setCoursesLoading", true);
    commit("setCoursesError", null);

    try {
      const response = await ParentService.getChildCourses(state.activeChildId);
      const courses = response.data.results || response.data || [];
      commit("setCourses", courses);
      return courses;
    } catch (error) {
      commit("setCoursesError", error);
      throw error;
    } finally {
      commit("setCoursesLoading", false);
    }
  },
  async loadActiveChildDashboard({ commit, state }) {
    if (!state.activeChildId) {
      commit("setDashboard", dashboardDefault);
      commit("setLastLoadedAt", new Date());
      return dashboardDefault;
    }

    commit("setDashboardLoading", true);
    commit("setDashboardError", null);

    try {
      const response = await ParentService.getChildDashboard(
        state.activeChildId,
        state.activePeriodId
      );
      const dashboard = mapDashboardResponse(response.data, state.dashboard);
      commit("setDashboard", dashboard);
      commit("setLastLoadedAt", new Date());
      return dashboard;
    } catch (error) {
      commit("setDashboardError", error);
      return state.dashboard;
    } finally {
      commit("setDashboardLoading", false);
    }
  },
  async loadActiveChildCourseOverview({ commit, state }, payload) {
    const courseId = typeof payload === "object" ? payload.courseId : payload;
    const period =
      typeof payload === "object" && payload.period
        ? payload.period
        : state.activePeriodId;
    const progressRange =
      typeof payload === "object" && payload.progressRange
        ? payload.progressRange
        : "last_4_months";

    if (!state.activeChildId || !courseId) {
      commit("setCourseOverview", { ...courseOverviewDefault });
      return state.courseOverview;
    }

    commit("setCourseOverviewLoading", true);
    commit("setCourseOverviewError", null);

    try {
      const response = await ParentService.getChildCourseOverview(
        state.activeChildId,
        courseId,
        period,
        progressRange
      );
      const overview = mapCourseOverviewResponse(
        response.data,
        state.dashboard
      );
      commit("setCourseOverview", overview);
      return overview;
    } catch (error) {
      commit("setCourseOverview", { ...courseOverviewDefault });
      commit("setCourseOverviewError", error);
      throw error;
    } finally {
      commit("setCourseOverviewLoading", false);
    }
  },
  async loadActiveChildProgress({ commit, state }) {
    if (!state.activeChildId) {
      commit(
        "setDashboard",
        mapProgressResponse({ points: [] }, state.dashboard)
      );
      commit("setLastLoadedAt", new Date());
      return state.dashboard.progress;
    }

    commit("setProgressLoading", true);
    commit("setProgressError", null);

    try {
      const response = await ParentService.getChildProgress(
        state.activeChildId,
        state.activeProgressRangeId
      );
      const dashboard = mapProgressResponse(response.data, state.dashboard);
      commit("setDashboard", dashboard);
      commit("setLastLoadedAt", new Date());
      return dashboard.progress;
    } catch (error) {
      commit("setProgressError", error);
      return state.dashboard.progress;
    } finally {
      commit("setProgressLoading", false);
    }
  },
  async loadActiveChildRecentEvents({ commit, state }) {
    if (!state.activeChildId) {
      commit(
        "setDashboard",
        mapRecentEventsResponse({ results: [] }, state.dashboard)
      );
      commit("setLastLoadedAt", new Date());
      return [];
    }

    commit("setRecentEventsLoading", true);
    commit("setRecentEventsError", null);

    try {
      const response = await ParentService.getChildRecentEvents(
        state.activeChildId,
        5
      );
      const dashboard = mapRecentEventsResponse(response.data, state.dashboard);
      commit("setDashboard", dashboard);
      commit("setLastLoadedAt", new Date());
      return dashboard.recentEvents;
    } catch (error) {
      commit("setRecentEventsError", error);
      return state.dashboard.recentEvents;
    } finally {
      commit("setRecentEventsLoading", false);
    }
  },
  async loadActiveChildActivityCenter({ commit, state }) {
    if (!state.activeChildId) {
      const activityCenter = createActivityCenterState();
      commit("setActivityCenter", activityCenter);
      return activityCenter;
    }

    commit("setActivityCenterLoading", true);
    commit("setActivityCenterError", null);

    try {
      const response = await ParentService.getChildActivityCenter(
        state.activeChildId,
        getActivityCenterRequestParams(state.activityCenter)
      );
      const activityCenter = mapActivityCenterResponse(
        response.data,
        state.activityCenter
      );
      commit("setActivityCenter", activityCenter);
      return activityCenter;
    } catch (error) {
      commit("setActivityCenterError", error);
      return state.activityCenter;
    } finally {
      commit("setActivityCenterLoading", false);
    }
  },
  async loadActivityEventDetail({ commit, state }, eventId) {
    if (!state.activeChildId || !eventId) {
      return null;
    }

    try {
      const response = await ParentService.getChildActivityEventDetail(
        state.activeChildId,
        eventId
      );
      return normalizeActivityEvent(response.data);
    } catch (error) {
      commit("setActivityCenterError", error);
      return null;
    }
  },
  async loadActiveChildDiary({ commit, state }, params) {
    if (!state.activeChildId) {
      const diary = {
        ...diaryDefault,
        period: { ...diaryDefault.period },
        events: [],
      };
      commit("setDiary", diary);
      return diary;
    }

    commit("setDiaryLoading", true);
    commit("setDiaryError", null);

    try {
      const response = await ParentService.getChildDiaryEvents(
        state.activeChildId,
        params
      );
      const diary = {
        period: response.data.period || { ...diaryDefault.period },
        student: response.data.student || null,
        events: response.data.events || [],
      };
      commit("setDiary", diary);
      return diary;
    } catch (error) {
      commit("setDiaryError", error);
      throw error;
    } finally {
      commit("setDiaryLoading", false);
    }
  },
  async loadActiveChildReportCard({ commit, state }) {
    if (!state.activeChildId) {
      commit(
        "setDashboard",
        mapReportCardResponse({ state: "empty" }, state.dashboard)
      );
      commit("setLastLoadedAt", new Date());
      return state.dashboard.reportCard;
    }

    commit("setReportCardLoading", true);
    commit("setReportCardError", null);

    try {
      const response = await ParentService.getChildReportCardPreview(
        state.activeChildId
      );
      const dashboard = mapReportCardResponse(response.data, state.dashboard);
      commit("setDashboard", dashboard);
      commit("setLastLoadedAt", new Date());
      return dashboard.reportCard;
    } catch (error) {
      commit("setReportCardError", error);
      return state.dashboard.reportCard;
    } finally {
      commit("setReportCardLoading", false);
    }
  },
  async downloadActiveChildReportCardPdf({ commit, state }) {
    if (!state.activeChildId) {
      return null;
    }

    commit("setReportCardDownloading", true);
    commit("setReportCardError", null);

    try {
      return await ParentService.getChildReportCardPdf(state.activeChildId);
    } catch (error) {
      commit("setReportCardError", error);
      throw error;
    } finally {
      commit("setReportCardDownloading", false);
    }
  },
  async loadActiveChildData({ dispatch }) {
    const results = await Promise.all([
      dispatch("loadActiveChildCourses"),
      dispatch("loadActiveChildDashboard"),
      dispatch("loadActiveChildProgress"),
      dispatch("loadActiveChildRecentEvents"),
      dispatch("loadActiveChildReportCard"),
    ]);
    return results;
  },
};

const mutations = {
  setChildren(state, children) {
    state.children = children;

    const hasActiveChild = children.some((child) => {
      return child.id === state.activeChildId;
    });

    if (!hasActiveChild) {
      state.activeChildId = children.length ? children[0].id : null;
    }
  },
  setActiveChildId(state, childId) {
    state.activeChildId = childId;
  },
  setActivePeriodId(state, periodId) {
    state.activePeriodId = periodId;
  },
  setActiveProgressRangeId(state, rangeId) {
    state.activeProgressRangeId = rangeId;
  },
  setCourses(state, courses) {
    state.courses = courses;
  },
  setDashboard(state, dashboard) {
    state.dashboard = dashboard;
  },
  setCourseOverview(state, overview) {
    state.courseOverview = overview;
  },
  setActivityCenter(state, activityCenter) {
    state.activityCenter = activityCenter;
  },
  setDiary(state, diary) {
    state.diary = diary;
  },
  setActivityFilter(state, { key, value, resetDate = false }) {
    state.activityCenter.filters = {
      ...state.activityCenter.filters,
      [key]: value,
      date: resetDate ? null : state.activityCenter.filters.date,
    };
    state.activityCenter.calendar = {
      ...state.activityCenter.calendar,
      selectedDate: resetDate
        ? null
        : state.activityCenter.calendar.selectedDate,
    };
    state.activityCenter.pagination = {
      ...state.activityCenter.pagination,
      page: 1,
    };
  },
  setActivityContext(state, { period, subject, type, status, search, date, page, pageSize }) {
    state.activityCenter.filters = {
      ...state.activityCenter.filters,
      period: period || state.activityCenter.filters.period,
      subject: subject || state.activityCenter.filters.subject,
      type: type || state.activityCenter.filters.type,
      status: status || state.activityCenter.filters.status,
      search: search !== undefined ? search : state.activityCenter.filters.search,
      date: date !== undefined ? date : null,
    };
    state.activityCenter.calendar = {
      ...state.activityCenter.calendar,
      selectedDate: date !== undefined ? date : null,
    };
    state.activityCenter.pagination = {
      ...state.activityCenter.pagination,
      page: Number.isFinite(page) && page > 0 ? page : 1,
      pageSize: Number.isFinite(pageSize) && pageSize > 0
        ? pageSize
        : state.activityCenter.pagination.pageSize,
    };
  },
  setActivityDate(state, date) {
    state.activityCenter.filters = {
      ...state.activityCenter.filters,
      date,
    };
    state.activityCenter.calendar = {
      ...state.activityCenter.calendar,
      selectedDate: date,
    };
    state.activityCenter.pagination = {
      ...state.activityCenter.pagination,
      page: 1,
    };
  },
  clearActivityFilters(state) {
    state.activityCenter.filters = {
      ...activityCenterDefault.filters,
    };
    state.activityCenter.calendar = {
      ...state.activityCenter.calendar,
      selectedDate: null,
    };
    state.activityCenter.pagination = {
      ...state.activityCenter.pagination,
      page: 1,
    };
  },
  setActivityPage(state, page) {
    state.activityCenter.pagination = {
      ...state.activityCenter.pagination,
      page,
    };
  },
  setActivityPageSize(state, pageSize) {
    state.activityCenter.pagination = {
      ...state.activityCenter.pagination,
      page: 1,
      pageSize,
    };
  },
  setActivityCalendarMonth(state, { year, month }) {
    state.activityCenter.calendar = {
      ...state.activityCenter.calendar,
      year,
      month,
      selectedDate: null,
    };
    state.activityCenter.filters = {
      ...state.activityCenter.filters,
      date: null,
    };
    state.activityCenter.pagination = {
      ...state.activityCenter.pagination,
      page: 1,
    };
  },
  setLastLoadedAt(state, lastLoadedAt) {
    state.lastLoadedAt = lastLoadedAt;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setCoursesLoading(state, loading) {
    state.coursesLoading = loading;
  },
  setDashboardLoading(state, loading) {
    state.dashboardLoading = loading;
  },
  setCourseOverviewLoading(state, loading) {
    state.courseOverviewLoading = loading;
  },
  setProgressLoading(state, loading) {
    state.progressLoading = loading;
  },
  setRecentEventsLoading(state, loading) {
    state.recentEventsLoading = loading;
  },
  setActivityCenterLoading(state, loading) {
    state.activityCenterLoading = loading;
  },
  setDiaryLoading(state, loading) {
    state.diaryLoading = loading;
  },
  setReportCardLoading(state, loading) {
    state.reportCardLoading = loading;
  },
  setReportCardDownloading(state, loading) {
    state.reportCardDownloading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setCoursesError(state, error) {
    state.coursesError = error;
  },
  setDashboardError(state, error) {
    state.dashboardError = error;
  },
  setCourseOverviewError(state, error) {
    state.courseOverviewError = error;
  },
  setProgressError(state, error) {
    state.progressError = error;
  },
  setRecentEventsError(state, error) {
    state.recentEventsError = error;
  },
  setActivityCenterError(state, error) {
    state.activityCenterError = error;
  },
  setDiaryError(state, error) {
    state.diaryError = error;
  },
  setReportCardError(state, error) {
    state.reportCardError = error;
  },
  resetStore(state) {
    Object.assign(state, createState());
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
